/**
 * Profile.js
 * User profile information and password change page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import moment from 'moment';
import { authService, roleService } from '../../services/api';
import EmailWarningModal from '../Common/EmailWarningModal';
import { checkEmailsForWarnings } from '../../utils/emailValidation';

const Profile = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [errors, setErrors] = useState({});
  const [roleLabelMap, setRoleLabelMap] = useState({});

  const [currentUser, setCurrentUser] = useState(() => authService.getCurrentUser());
  const currentUserRoles = currentUser?.roles || [];

  const [isEditingLoginEmail, setIsEditingLoginEmail] = useState(false);
  const [newLoginEmail, setNewLoginEmail] = useState('');
  const [loginEmailLoading, setLoginEmailLoading] = useState(false);
  const [loginEmailError, setLoginEmailError] = useState('');

  // Email validation warning modal state
  const [emailWarningState, setEmailWarningState] = useState({
    warnings: [],
    onIgnore: null,
  });

  const loadRoleLabels = useCallback(async () => {
    try {
      const result = await roleService.getAppRoles();
      if (result.success && Array.isArray(result.data)) {
        const nextMap = result.data.reduce((acc, role) => {
          acc[role.name] = role.label || role.name;
          return acc;
        }, {});
        setRoleLabelMap(nextMap);
      }
    } catch (error) {
      console.error('Error loading role labels:', error);
    }
  }, []);

  useEffect(() => {
    loadRoleLabels();
  }, [loadRoleLabels]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'La password corrente è richiesta';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'La nuova password è richiesta';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'La password deve essere di almeno 6 caratteri';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Conferma la nuova password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Le password non coincidono';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setAlert({ show: false, type: '', message: '' });

    try {
      const result = await authService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (result.success) {
        setAlert({
          show: true,
          type: 'success',
          message: 'Password cambiata con successo!'
        });

        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setAlert({
          show: true,
          type: 'danger',
          message: result.message || 'Errore durante il cambio password'
        });
      }
    } catch (error) {
      console.error('Change password error:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Errore di connessione al server'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLoginEmail = async (e, bypassEmailWarning = false) => {
    if (e && e.preventDefault) e.preventDefault();

    const trimmed = newLoginEmail.trim();
    if (!trimmed) {
      setLoginEmailError('L\'email di login è obbligatoria');
      return;
    }

    if (!bypassEmailWarning) {
      const warnings = checkEmailsForWarnings([
        {
          label: 'Nuova Email di Login',
          value: trimmed,
          selector: 'input[data-testid="new-login-email-input"]',
        },
      ]);
      if (warnings.length > 0) {
        setEmailWarningState({
          warnings,
          onIgnore: () => handleSaveLoginEmail(null, true),
        });
        return;
      }
    }

    try {
      setLoginEmailLoading(true);
      setLoginEmailError('');
      setAlert({ show: false, type: '', message: '' });

      const result = await authService.changeLoginEmail(trimmed);

      if (result.success) {
        setAlert({
          show: true,
          type: 'success',
          message: result.message || 'Email di login aggiornata con successo!'
        });
        setIsEditingLoginEmail(false);
        setCurrentUser(prev => ({
          ...prev,
          loginEmail: trimmed
        }));
      } else {
        setLoginEmailError(result.message || 'Errore durante la modifica dell\'email di login');
      }
    } catch (error) {
      console.error('Change login email error:', error);
      setLoginEmailError('Errore di connessione al server');
    } finally {
      setLoginEmailLoading(false);
    }
  };

  const currentLoginEmail = currentUser?.loginEmail || currentUser?.email || '';

  return (
    <Container>
      <Row className="justify-content-center">
        <Col lg={10}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="fas fa-user-circle me-2"></i>
              Profilo
            </h2>
          </div>

          {alert.show && (
            <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false, type: '', message: '' })}>
              {alert.message}
            </Alert>
          )}

          <Row>
            <Col lg={6} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-user me-2"></i>
                    Informazioni Profilo
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Nome:</strong> {currentUser?.firstName} {currentUser?.lastName}
                  </div>
                  <div className="mb-3">
                    <strong>Email di Contatto:</strong> {currentUser?.email || '-'}
                  </div>
                  {currentUser?.dateOfBirth && (
                    <div className="mb-3">
                      <strong>Data di Nascita:</strong> {moment(currentUser.dateOfBirth).format('DD/MM/YYYY')}
                    </div>
                  )}
                  <div className="mb-3">
                    <strong>Ruoli:</strong> {currentUserRoles.map(role => roleLabelMap[role] || role).join(', ') || '-'}
                  </div>

                  <hr className="my-3" />

                  <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="fw-bold mb-0">
                        <i className="fas fa-envelope me-1 text-primary"></i> Email di Login:
                      </label>
                      {!isEditingLoginEmail && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setIsEditingLoginEmail(true);
                            setNewLoginEmail(currentLoginEmail);
                            setLoginEmailError('');
                          }}
                          data-testid="edit-login-email-btn"
                        >
                          <i className="fas fa-pencil-alt me-1"></i> Modifica
                        </Button>
                      )}
                    </div>

                    {!isEditingLoginEmail ? (
                      <div className="p-2 bg-light rounded border">
                        <span className="font-monospace text-primary fw-bold" data-testid="current-login-email">
                          {currentLoginEmail || '-'}
                        </span>
                      </div>
                    ) : (
                      <Form onSubmit={handleSaveLoginEmail} className="mt-2 p-2 bg-light rounded border">
                        <Form.Group className="mb-2">
                          <Form.Label className="small text-muted mb-1">Nuova Email di Login *</Form.Label>
                          <Form.Control
                            type="email"
                            value={newLoginEmail}
                            onChange={(e) => {
                              setNewLoginEmail(e.target.value);
                              if (loginEmailError) setLoginEmailError('');
                            }}
                            isInvalid={!!loginEmailError}
                            placeholder="Inserisci la nuova email di login"
                            data-testid="new-login-email-input"
                            disabled={loginEmailLoading}
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            {loginEmailError}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <div className="d-flex gap-2">
                          <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            disabled={loginEmailLoading}
                            data-testid="save-login-email-btn"
                          >
                            {loginEmailLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" /> Salvataggio...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i> Salva
                              </>
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            disabled={loginEmailLoading}
                            onClick={() => {
                              setIsEditingLoginEmail(false);
                              setLoginEmailError('');
                            }}
                          >
                            Annulla
                          </Button>
                        </div>
                      </Form>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-lock me-2"></i>
                    Cambia Password
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password Corrente *</Form.Label>
                      <Form.Control
                        type="password"
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        isInvalid={!!errors.currentPassword}
                        placeholder="Inserisci la password corrente"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.currentPassword}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Nuova Password *</Form.Label>
                      <Form.Control
                        type="password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        isInvalid={!!errors.newPassword}
                        placeholder="Inserisci la nuova password (min. 6 caratteri)"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.newPassword}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Conferma Nuova Password *</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        isInvalid={!!errors.confirmPassword}
                        placeholder="Conferma la nuova password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-grid mt-4">
                      <Button 
                        type="submit" 
                        variant="primary" 
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Cambiando password...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            Cambia Password
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Email Warning Modal */}
      <EmailWarningModal
        show={emailWarningState.warnings.length > 0}
        warnings={emailWarningState.warnings}
        onCorrect={() => setEmailWarningState({ warnings: [], onIgnore: null })}
        onIgnoreAndProceed={() => {
          const proceed = emailWarningState.onIgnore;
          setEmailWarningState({ warnings: [], onIgnore: null });
          if (proceed) proceed();
        }}
      />
    </Container>
  );
};

export default Profile;
